import { forwardRef, useId } from 'react';
import { Input, type InputProps } from '../ui/Input';
import { FieldLabel } from './FieldLabel';
import { useNumericInput, type NumericInputValue } from '@/hooks/useNumericInput';

interface TextFieldBaseProps extends Omit<InputProps, 'value'> {
  required?: boolean;
  optional?: boolean;
  labelVariant?: 'bold' | 'caps';
  /**
   * Figma Step-1 style: no visible label; the label (plus `*` when required)
   * renders as the placeholder instead.
   */
  floating?: boolean;
  /**
   * Figma financial-field style: bordered shell with a small teal label at the
   * top and the value (with adjacent prefix, e.g. "$0.00") beneath it.
   */
  inset?: boolean;
  /** Grey helper line under the field. */
  helpText?: string;
}

/** The ordinary text input: value handling is whatever the caller passes (e.g. `register`). */
interface PlainTextFieldProps extends TextFieldBaseProps {
  numeric?: false;
  value?: InputProps['value'];
  groupThousands?: never;
  maxDigits?: never;
}

/**
 * Whole-number input (MYN-277, FR-183): renders `type="text"` with
 * `inputMode="numeric"`, drops every non-digit as the user types or pastes,
 * groups thousands live with the caret kept in place, and ignores digits past
 * `maxDigits` (default 13). It is **controlled**: `value` is required
 * (a digit string or a saved number; blank is `''`/`null`/`undefined`) and
 * `onChange` receives the event with `target.value` set to the PLAIN digits, so
 * a react-hook-form `Controller` binds with `<TextField numeric {...field} />`.
 * Do not spread `register()` into it - a register-driven input is uncontrolled
 * and would not show a saved value grouped.
 */
interface NumericTextFieldProps extends TextFieldBaseProps {
  numeric: true;
  value: NumericInputValue;
  /** Group thousands in what the user sees. Default true; off for a year. */
  groupThousands?: boolean;
  /**
   * Most digits accepted; extra digits are ignored. Default `NUMERIC_INPUT_MAX_DIGITS` (13,
   * money). Use `NUMERIC_INPUT_MAX_DIGITS_COUNT` (9) for `Int` columns and
   * `NUMERIC_INPUT_MAX_DIGITS_YEAR` (4) for a year.
   */
  maxDigits?: number;
}

export type TextFieldProps = PlainTextFieldProps | NumericTextFieldProps;

/** Input with a visible label above it (or floating placeholder-as-label). */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    label,
    required = false,
    optional = false,
    labelVariant = 'caps',
    floating = false,
    inset = false,
    helpText,
    numeric = false,
    groupThousands,
    maxDigits,
    ...rest
  },
  ref,
) {
  const inputId = useId();
  const { value, ...restProps } = rest;
  const numericInput = useNumericInput({
    value: value as NumericInputValue,
    onChange: rest.onChange,
    groupThousands,
    maxDigits,
  });
  const inputProps: Omit<InputProps, 'label'> = numeric
    ? {
        ...restProps,
        type: 'text',
        inputMode: 'numeric',
        value: numericInput.displayValue,
        onChange: numericInput.onChange,
      }
    : { ...restProps, value: value as InputProps['value'] };

  if (inset) {
    const { prefix, error, className = '', ...bareProps } = inputProps;
    return (
      <div className="flex flex-col gap-1">
        <div className="rounded-button border border-input-border bg-white px-4 py-2.5 focus-within:border-transparent focus-within:ring-2 focus-within:ring-dkgreen">
          <label
            htmlFor={inputId}
            className="font-sans text-xs font-semibold text-teal2"
          >
            {label}
            {required && <span aria-hidden="true">*</span>}
          </label>
          <div className="flex items-baseline gap-1">
            {prefix && <span className="text-sm text-navy">{prefix}</span>}
            <Input
              ref={ref}
              id={inputId}
              label={label}
              variant="bare"
              className={className}
              {...bareProps}
            />
          </div>
        </div>
        {error && (
          <p role="alert" className="pl-1 text-xs text-danger">
            {error}
          </p>
        )}
        {helpText && <p className="pl-1 text-xs text-slate">{helpText}</p>}
      </div>
    );
  }

  const placeholder = floating
    ? (inputProps.placeholder ?? `${label}${required ? '*' : ''}`)
    : inputProps.placeholder;
  // Figma renders label-as-placeholder text in the muted teal tone.
  const floatingClass = floating ? 'placeholder:text-teal/70' : '';

  return (
    <div className="flex flex-col gap-1.5">
      {!floating && (
        <FieldLabel
          label={label}
          htmlFor={inputId}
          required={required}
          optional={optional}
          variant={labelVariant}
        />
      )}
      <Input
        ref={ref}
        id={inputId}
        label={label}
        {...inputProps}
        className={`${floatingClass} ${inputProps.className ?? ''}`.trim()}
        placeholder={placeholder}
      />
      {helpText && <p className="pl-1 text-xs text-slate">{helpText}</p>}
    </div>
  );
});

TextField.displayName = 'TextField';
